import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuth } from '../context/auth';
import { Button } from '../components/common/Button';
import ConfirmEmail from '../components/auth/ConfirmEmail';
import ConfirmationModal from '../components/auth/ConfirmationModal';
import { getAuthErrorTranslationKey, getEmailValidationKey } from '../utils/authErrors';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const { resetPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [isEmailConfirmationOpen, setIsEmailConfirmationOpen] = useState(false);
  const [isConfimationOpen, setIsConfimationOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValidationKey = getEmailValidationKey(email);
    if (emailValidationKey) {
      setError(t(emailValidationKey));
      return;
    }

    try {
      setLoading(true);
      await resetPassword(email.trim());
      setSuccess(true);
      setError('');
    } catch (err) {
      console.error('Failed to send password reset email', err);
      setError(t(getAuthErrorTranslationKey(err, 'errors.resetPassword')));
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resetPassword(email.trim());
    } catch (err) {
      console.error('Failed to resend password reset email', err);
      setError(t(getAuthErrorTranslationKey(err, 'errors.resetPassword')));
    }
  };

  if (success) {
    return (
      <ConfirmEmail
        email={email}
        onResend={handleResend}
        descriptionKey="resetPassword.resetLinkSent"
        backToPath="/login"
        backToTextKey="backToHome"
        resendTextKey="resetPassword.resend"
      />
    );
  }

  const text2 = {
    title: t('improveProfileStep.security.changePassword', { ns: 'auth' }),
    heading: t('improveProfileStep.security.passwordResetLinkSent', { ns: 'auth' }),
    securityNotice: t('improveProfileStep.security.passwordResetHint', { ns: 'auth' }),
    emailInstruction: t('improveProfileStep.security.passwordResetHint', { ns: 'auth' }),
    emailHighlight: email,
    closeButton: t('confirmEmail.close', { ns: 'auth' }),
    closeButtonHint: t('confirmEmail.confirmReceived', { ns: 'auth' }),
    resendButton: t('confirmEmail.resend', { ns: 'auth' }),
    editButton: t('changeEmail', { ns: 'auth' }),
  };

  const text3 = {
    title: t('improveProfileStep.security.changePassword', { ns: 'auth' }),
    heading: t('improveProfileStep.security.passwordResetEffective', { ns: 'auth' }),
    securityNotice: t('improveProfileStep.security.passwordResetConfirmed', { ns: 'auth' }),
    closeButton: t('confirmEmail.close', { ns: 'auth' }),
  };

  return (
    <>
      <div className="flex flex-col py-14 md:min-h-[60vh] md:justify-center md:items-center">
        <div>
          <h2 className="text-4xl font-bold text-secondary mb-4">{t('forgotPassword')}</h2>
          <form className="my-6" onSubmit={handleSubmit} noValidate>
            <label className="block font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary mb-2">
              {t('email')}
            </label>
            <div className="relative mb-6">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8CA2B3]">
                <Mail size={20} />
              </span>
              <input
                type="email"
                className="w-full bg-[#E1E9F0] border-0 px-10 py-3 font-ambit text-base font-normal text-secondary focus:outline-none"
                placeholder={t('emailPlaceholder')}
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                required
              />
            </div>
            {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

            <Button type="submit" disabled={loading || !email} className="w-full">
              {loading ? t('saving') : t('resetPassword.resetPasswordCta')}
            </Button>
          </form>

          <div className="flex justify-center">
            <Link
              to="/login"
              className="text-secondary font-ambit text-base font-bold underline underline-offset-2"
            >
              {t('backToHome')}
            </Link>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isEmailConfirmationOpen}
        text={text2}
        onClose={() => {
          setIsEmailConfirmationOpen(false);
        }}
        onResend={() => {
          handleResend();
          setIsEmailConfirmationOpen(false);
          setIsConfimationOpen(true);
        }}
      />

      <ConfirmationModal
        isOpen={isConfimationOpen}
        text={text3}
        onClose={() => {
          setIsConfimationOpen(false);
        }}
      />
    </>
  );
};

export default ForgotPassword;
